import { MouseEventHandler, useContext, useMemo, useRef, useState } from 'react';
import { InventoryFileEntity, isActiveVersionAVersion } from '@/redux/slices/slice_types';
import PolarisIcon from '../PolarisIcon';
import { MEDIA_INFO_TYPES } from '@/constants/mediaConstants';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectFileTypeGroupings, selectFileTypes } from '@/redux/selectors/constantsSelectors';
import { selectCanDownloadInventory, selectCanViewInventory, selectCanViewMediaInfo } from '@/redux/selectors/authSelectors';
import axios from '@/_utils/axios';
import baseAxios from 'axios';
import { getUnsupportedPreviewFormats, storeFile, useHistory } from '@/_utils/utils';
import { useToast } from '../Toast';
import { deleteFileById, setPreviewFile } from '@/redux/slices/orderPlacementSlice';
import Dialog from '../Dialog';
import { selectActiveVersion, selectTitleId } from '@/redux/selectors/titleSelectors';
import { selectActiveVersion as selectActiveVersionV2, selectActiveTitleId } from '@/redux/selectors/titleV2Selectors';
import OverflowMenuItem from '../OverflowMenu/OverflowMenuItem';
import OverflowMenu from '../OverflowMenu/OverflowMenu';
import InventoryFileMediaInfo from './InventoryFileMediaInfo';
import styled from '@emotion/styled';
import { IconButton } from '@mui/material';
import ConditionalTooltip from '../ConditionalTooltip/ConditionalTooltip';
import DownloadConnectorModal from '@/components/FileManager/DownloadConnectorModal';
import { SnackbarKey } from 'notistack';
import { applyFilter, clearFilters, setCurrentTable } from '@/redux/slices/dataTableSlice';
import { ConfigContext } from '@/contextAPI/configContext';

interface InventoryFileOverflowMenuProps {
  file: InventoryFileEntity;
  onRemove?: () => void;
  disableDownloads?: boolean;
  disablePreviews?: boolean;
  canDelete?: boolean;
  previewOnly?: boolean;
  xOnly?: boolean;
}

const InventoryFileOverflowMenu = ({
  file,
  onRemove,
  disableDownloads,
  disablePreviews,
  canDelete,
  previewOnly = false,
  xOnly = false
}: InventoryFileOverflowMenuProps) => {
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [isDownloading, setIsDownloading] = useState<number | null>(null);
  const { success, error, load, closeSnackbar } = useToast();
  const canDownloadInventory = useAppSelector(selectCanDownloadInventory);
  const canPreviewInventory = useAppSelector(selectCanViewInventory);
  const filetype = '.' + file.file_type;
  const canViewMediaInfo = MEDIA_INFO_TYPES.includes(filetype) && useAppSelector(selectCanViewMediaInfo);
  const fileTypes = useAppSelector(selectFileTypes);
  const fileType = fileTypes[file.file_type] || 'other';
  const fileTypeGroupings: any = useAppSelector(selectFileTypeGroupings);
  const disabledFileTypes = getUnsupportedPreviewFormats(fileTypeGroupings.other);
  const activeVersion = useAppSelector(isTitleV2 ? selectActiveVersionV2 : selectActiveVersion);
  const id = useAppSelector(isTitleV2 ? selectActiveTitleId : selectTitleId);
  const noPermissionMessage = 'You do not have permissions for this action.';
  const [openDownloadConnectorModal, setOpenDownloadConnectorModal] = useState(false);
  const [dialog, setDialog] = useState<any>(null);
  const [hasBeenOverridden, setHasBeenOverridden] = useState(false);
  const snackbarIdRef = useRef<SnackbarKey | null>(null);
  const fileNameNoPath = useMemo(() => file.name?.split('/').pop() || '', [file]);
  const [loading, setLoading] = useState(false);

  const isPreviewDisabled = useMemo(() => {
    return file.label?.toLowerCase() !== 'sdviproxy' && (file.file_size > 5e9 || disabledFileTypes.includes(file.file_type));
  }, [file, fileType]);

  const handleDownload = async (file: InventoryFileEntity) => {
    setIsDownloading(file.id);
    snackbarIdRef.current = await load('Downloading file...', {
      autoHideDuration: null
    });

    const downloadFromUrl = async (url: string) =>
      url && url.length > 0
        ? await baseAxios({ url, method: 'GET', responseType: 'blob' })
            .then((res) => storeFile(res, file.name))
            .catch(() => error('There was an error downloading the file.'))
            .finally(() => {
              if (snackbarIdRef.current) {
                closeSnackbar(snackbarIdRef.current);
                snackbarIdRef.current = null;
              }
              setIsDownloading(null);
            })
        : null;

    try {
      if (
        (fileType === 'image' || fileType === 'video' || fileType === 'audio') &&
        file?.is_rally_inventory === false &&
        (file?.non_rally_inventory_download_url || '').length > 0
      ) {
        downloadFromUrl(file?.non_rally_inventory_download_url);
        return;
      }

      if (file?.is_rally_inventory && file?.file_path && file?.file_origin_id && file?.download_connector_name) {
        setOpenDownloadConnectorModal(true);
      } else if (isActiveVersionAVersion(activeVersion) || file.version_id) {
        axios.get(`/inventory/titles/${activeVersion?.id || file.version_id || id}/files/${file.id}/`).then((res) => {
          downloadFromUrl(res.data?.data?.url);
        });
      } else {
        closeSnackbar(snackbarIdRef.current);
      }
    } catch {
      error('There was an error downloading the file.');
      setIsDownloading(null);
    }
  };

  const handleSetPreview: MouseEventHandler<any> = (e) => {
    e.stopPropagation();
    dispatch(setPreviewFile(file));
  };

  const confirmDeleteFile = async () => {
    if (!isActiveVersionAVersion(activeVersion)) return;
    setLoading(true);

    axios
      .delete(`/inventory/titles/${activeVersion.id}/files/${file.id}/`)
      .then((res) => {
        if (!res.data?.data) {
          throw new Error();
        } else {
          dispatch(deleteFileById(file.id));
          success('File Deleted');
          setDialog(null);
        }
      })
      .catch((err) => {
        if (err?.data?.data?.data?.job_ids?.length) {
          const jobIds = err?.data?.data?.data?.job_ids.map((v: string) => 'J' + v);
          setDialog({
            title: 'Cannot Delete File with Active Jobs',
            submitText: 'View Jobs',
            onSubmit: () => {
              dispatch(setCurrentTable('jobs'));
              dispatch(clearFilters());
              dispatch(applyFilter({ type: 'filter_id', selections: [jobIds, 'job_hid_filter'] }));
              setDialog(null);
              history.push('/jobs');
            },
            content: (
              <>
                <div>This file is in use by {jobIds.length} activejob(s).</div>
                <div>Please wait for them to complete or cancel them before deleting the file.</div>
              </>
            )
          });
        } else {
          setDialog(null);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleOverride = () => {
    if (!isActiveVersionAVersion(activeVersion)) {
      throw new Error('`activeVersion` is not a version');
    }

    axios
      .post(`/inventory/titles/${activeVersion.id || id}/files/${file.id}/override_qc_fail/`)
      .then(() => {
        success('QC Status has been successfully overridden. This process may take a few minutes.');
        setHasBeenOverridden(true);
      })
      .catch(() => {
        error('There was an error overriding the file.');
      })
      .finally(() => {
        setDialog(null);
      });
  };

  const openDialog = (view: string) => {
    const dialogOptions = {
      delete: {
        title: 'Are you sure you want to delete this file?',
        submitText: 'Yes, delete this file',
        onSubmit: confirmDeleteFile,
        content: `${fileNameNoPath || 'This file'} will be deleted from the inventory.`
      },
      override: {
        title: 'Are you sure you want to override this QC Status?',
        submitText: 'Yes, override this file',
        onSubmit: handleOverride,
        content: 'Doing so will manually mark the QC status as passed.'
      }
    };

    setDialog(dialogOptions[view as keyof typeof dialogOptions]);
  };

  const availableOverflowOptions = +(
    canDelete ||
    canViewMediaInfo ||
    (!['missing', 'future', 'archive'].includes(file.status) && !onRemove && canDownloadInventory) ||
    (!['missing', 'future', 'archive'].includes(file.status) && !onRemove && canPreviewInventory) ||
    (file?.is_qc_failed ?? false)
  );

  return previewOnly ? (
    <PreviewOnlyDiv disabled={+(xOnly ? true : isPreviewDisabled)}>
      <ConditionalTooltip title="Preview is not supported for this file type." show={!xOnly && isPreviewDisabled}>
        <div>
          <IconButton onClick={handleSetPreview} disabled={isPreviewDisabled}>
            <PolarisIcon height={20} icon={isPreviewDisabled ? 'hidden' : 'preview'} />
          </IconButton>
        </div>
      </ConditionalTooltip>
    </PreviewOnlyDiv>
  ) : xOnly && onRemove ? (
    <XOnlyDiv>
      <IconButton onClick={onRemove}>
        <PolarisIcon height={21} icon="close" />
      </IconButton>
    </XOnlyDiv>
  ) : (
    <MenuWrapperDiv>
      <OverflowMenu
        style={{
          opacity: availableOverflowOptions,
          cursor: availableOverflowOptions ? 'pointer' : 'none',
          pointerEvents: availableOverflowOptions ? 'auto' : 'none'
        }}
      >
        {canDelete && (
          <OverflowMenuItem
            onClick={() => {
              openDialog('delete');
            }}
            disabled={!canDelete}
            disabledTooltipText={noPermissionMessage}
          >
            <PolarisIcon height={15} icon="cancel" />
            Delete
          </OverflowMenuItem>
        )}
        {canViewMediaInfo && <InventoryFileMediaInfo file={file} />}
        {!['missing', 'future', 'archive'].includes(file.status) && !onRemove && canDownloadInventory && (
          <OverflowMenuItem
            onClick={() => {
              handleDownload(file);
            }}
            disabled={disableDownloads || isDownloading === file.id}
            disabledTooltipText={disableDownloads ? noPermissionMessage : 'This file is currently downloading.'}
          >
            <PolarisIcon height={15} icon={'download'} />
            Download
          </OverflowMenuItem>
        )}
        {!['missing', 'future', 'archive'].includes(file.status) && !onRemove && canPreviewInventory && (
          <OverflowMenuItem
            onClick={handleSetPreview}
            disabled={disablePreviews || isPreviewDisabled}
            disabledTooltipText={'Preview is not supported for this file type.'}
          >
            <PolarisIcon height={15} icon={isPreviewDisabled ? 'hidden' : 'preview'} />
            Preview
          </OverflowMenuItem>
        )}
        {file?.is_qc_failed && (
          <OverflowMenuItem
            onClick={() => {
              if (hasBeenOverridden) {
                error('QC override in progress, and may take a few minutes to complete.');
              } else {
                openDialog('override');
              }
            }}
          >
            <PolarisIcon height={15} icon="mdi:file-replace-outline" />
            QC Override
          </OverflowMenuItem>
        )}
      </OverflowMenu>
      <Dialog
        open={dialog !== null}
        title={dialog?.title}
        submitText={dialog?.submitText}
        onSubmit={dialog?.onSubmit}
        cancelText={'Cancel'}
        maxWidth={480}
        loading={loading}
        onCancel={() => {
          setDialog(null);
        }}
      >
        {dialog?.content}
      </Dialog>
      <DownloadConnectorModal file={file} open={openDownloadConnectorModal} onClose={() => setOpenDownloadConnectorModal(false)} />
    </MenuWrapperDiv>
  );
};

export default InventoryFileOverflowMenu;

const PreviewOnlyDiv = styled.div<{ disabled: number }>`
  height: 100%;
  padding: ${({ theme }) => theme.spacing(1)};
  opacity: ${({ disabled }) => (disabled ? 0.3 : 0.66)};
`;

const MenuWrapperDiv = styled.div`
  padding: ${({ theme }) => theme.spacing(1)};
`;

const XOnlyDiv = styled.div`
  padding: ${({ theme }) => theme.spacing(1)};

  .MuiIconButton-root {
    opacity: 0.5;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }
  }
`;
